import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import api from "../lib/api";
import { Upload, Trash2, Save, Loader2, ImageIcon, User, Eye } from "lucide-react";

export default function CoachSettings() {
    const [branding, setBranding] = useState({ display_name: "", logo_url: "", has_logo: false });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        api.get("/coach/branding")
            .then(r => setBranding(r.data))
            .catch(() => toast.error("Failed to load branding"))
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        if (!branding.display_name.trim()) {
            toast.error("Display name is required");
            return;
        }
        setSaving(true);
        try {
            await api.put("/coach/branding", { display_name: branding.display_name });
            toast.success("Branding saved");
        } catch {
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const allowed = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"];
        if (!allowed.includes(file.type)) {
            toast.error("Unsupported format. Use PNG, JPG, SVG, or WebP.");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("File too large. Max 5MB.");
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/coach/branding/logo", form, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            setBranding(b => ({ ...b, logo_url: data.logo_url, has_logo: true }));
            toast.success("Logo uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const handleRemoveLogo = async () => {
        try {
            await api.delete("/coach/branding/logo");
            setBranding(b => ({ ...b, logo_url: "", has_logo: false }));
            toast.success("Logo removed");
        } catch {
            toast.error("Failed to remove logo");
        }
    };

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

    if (loading) return (
        <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-3xl space-y-6" data-testid="coach-settings">
            <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-foreground" style={{ fontFamily: "Outfit" }}>
                    Settings
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Manage your report branding and profile</p>
            </div>

            {/* Branding Section */}
            <Card className="border-border bg-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                        <User className="h-4 w-4 text-[#4A7AB5]" /> Report Branding
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                        Personalize how your name and logo appear on assessment reports
                    </p>
                </CardHeader>
                <CardContent className="space-y-5">
                    {/* Display Name */}
                    <div>
                        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                            Coach Display Name *
                        </label>
                        <input data-testid="branding-display-name"
                            className="w-full text-sm text-foreground bg-background border border-border rounded-lg px-4 py-2.5 outline-none focus:border-[#4A7AB5] focus:ring-2 focus:ring-[#4A7AB5]/10 transition-all"
                            value={branding.display_name}
                            onChange={e => setBranding(b => ({ ...b, display_name: e.target.value }))}
                            placeholder="e.g. Rajiv Mehta Executive Coaching" />
                        <p className="text-xs text-muted-foreground mt-1.5">
                            This name appears in the report header alongside your logo
                        </p>
                    </div>

                    <Separator className="bg-border" />

                    {/* Logo Upload */}
                    <div>
                        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                            Coach Logo (Optional)
                        </label>

                        {branding.has_logo && branding.logo_url ? (
                            <div className="flex items-center gap-4" data-testid="logo-preview">
                                <div className="w-20 h-20 rounded-xl border border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                                    <img src={`${backendUrl}${branding.logo_url}`} alt="Coach logo"
                                        className="max-w-full max-h-full object-contain" />
                                </div>
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground">Logo uploaded</p>
                                    <div className="flex gap-2">
                                        <Button data-testid="replace-logo-btn" variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                                            disabled={uploading} className="gap-1.5 text-xs">
                                            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Replace
                                        </Button>
                                        <Button data-testid="remove-logo-btn" variant="outline" size="sm" onClick={handleRemoveLogo}
                                            className="gap-1.5 text-xs text-destructive hover:text-destructive">
                                            <Trash2 className="h-3 w-3" /> Remove
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div data-testid="logo-upload-area"
                                className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-[#4A7AB5]/40 transition-colors cursor-pointer"
                                onClick={() => fileRef.current?.click()}>
                                <ImageIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                                <p className="text-sm text-muted-foreground">Click to upload your logo</p>
                                <p className="text-xs text-muted-foreground/60 mt-1">PNG, JPG, SVG, or WebP. Max 5MB.</p>
                                {uploading && <Loader2 className="h-4 w-4 animate-spin mx-auto mt-2 text-[#4A7AB5]" />}
                            </div>
                        )}
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                            className="hidden" onChange={handleUpload} />
                    </div>

                    <Separator className="bg-border" />

                    {/* Save */}
                    <div className="flex items-center justify-between">
                        <Button data-testid="save-branding-btn" onClick={handleSave} disabled={saving}
                            style={{ backgroundColor: "#4A7AB5" }} className="gap-2">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Save Branding
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Preview */}
            <Card className="border-border bg-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                        <Eye className="h-4 w-4 text-[#4A7AB5]" /> Report Header Preview
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div data-testid="branding-preview" className="rounded-xl overflow-hidden border border-border">
                        {/* Mock report header */}
                        <div className="bg-[#1E3A5F] px-6 py-5 flex items-center gap-4">
                            {branding.has_logo && branding.logo_url ? (
                                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                                    <img src={`${backendUrl}${branding.logo_url}`} alt="Logo"
                                        className="max-w-full max-h-full object-contain" />
                                </div>
                            ) : null}
                            <div>
                                <h3 className="text-white font-semibold text-base" style={{ fontFamily: "Outfit" }}>
                                    Coachability Diagnostic Report
                                </h3>
                                <p className="text-white/70 text-xs mt-0.5">
                                    {branding.display_name || "Coach Name"}
                                </p>
                            </div>
                        </div>
                        <div className="px-6 py-3 bg-muted/30 text-center">
                            <span className="text-[0.6rem] text-muted-foreground/50 tracking-wide">
                                Powered by K2B Learning Pvt. Ltd.
                            </span>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
